import { FC, memo, useCallback, useState } from 'react'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { ChatStartUserTabToolbar } from './components/chatStartUserTabToolbar/chatStartUserTabToolbar'
import { ChatStartUserTabUserList } from './components/chatStartUserTabUserList/chatStartUserTabUserList'
import { useChatStartUserTabPanel } from './useChatStartUserTabPanel'
import { EntityType } from '@crew/enums/domain'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { getDefaultTabValue } from '@crew/utils/enum'
import { DirectMessageTabs } from 'enums/app'
import { useShowApiErrors } from 'hooks/useShowApiErrors'

type ChatStartUserTabPanelProps = {
  onClose: () => void
}

// ユーザータブ
export const ChatStartUserTabPanel: FC<ChatStartUserTabPanelProps> = memo(
  (props) => {
    const {
      insertDirectChannel,
      insertFavorite,
      isLoadingInsertProjectForDirectChannelMutation,
    } = useChatStartUserTabPanel()

    const { t } = useTranslation()
    const { navigate } = useCrewNavigate()
    const [showApiErrors] = useShowApiErrors()

    // 選択されたユーザID
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

    // ユーザ選択状態変更時
    const handleUserSelectionChange = useCallback((userId: string) => {
      // 選択されたユーザIDをセット
      setSelectedUserId(userId)
    }, [])

    // 開始ボタンクリック時
    const handleStartButtonClick = useCallback(async () => {
      if (!selectedUserId) return

      try {
        // ダイレクトチャンネルを作成
        const result = await insertDirectChannel(selectedUserId)
        if (!result.directChannel?.id) return

        // お気に入りとして登録
        await insertFavorite(EntityType.Project, result.directChannel?.id)

        // 選択したユーザーとのダイレクトチャンネル画面へ遷移
        navigate(
          `/direct-channels/${result.directChannel.id}/${getDefaultTabValue(
            DirectMessageTabs,
          )}`,
        )
      } catch (err) {
        showApiErrors(err)
        return
      }

      // ダイアログを閉じる
      props.onClose()
    }, [
      insertDirectChannel,
      insertFavorite,
      navigate,
      props,
      selectedUserId,
      showApiErrors,
    ])

    return (
      <div className="flex flex-col h-full gap-y-2">
        {/* ツールバー */}
        <CrewErrorBoundary>
          <ChatStartUserTabToolbar />
        </CrewErrorBoundary>

        {/* ユーザー一覧 */}
        <CrewScrollView className="flex-1 min-h-0">
          <div className="flex flex-col gap-y-5">
            {/* モーダルの最小幅を制限し、ユーザ一覧が正しく表示されるようにする */}
            <div className="overflow-x-auto">
              <div className="min-w-[400px] flex flex-col gap-y-2.5 p-2">
                <ChatStartUserTabUserList
                  onUserSelected={handleUserSelectionChange}
                  isLoading={isLoadingInsertProjectForDirectChannelMutation}
                />
              </div>
            </div>
          </div>
        </CrewScrollView>

        <div className="flex justify-end gap-x-2">
          {/* 開始ボタン */}
          <CrewButton
            text={t('action.start')}
            type="primary"
            stylingMode="contained"
            onClick={handleStartButtonClick}
            disabled={
              !selectedUserId || isLoadingInsertProjectForDirectChannelMutation
            }
          />
          {/* 閉じるボタン */}
          <CrewButton
            text={t('action.close')}
            type="normal"
            stylingMode="outlined"
            onClick={props.onClose}
          />
        </div>
      </div>
    )
  },
)
